"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 0: NROM
 * ------------------------------------------------------------------ *
 * Simplest mapper: no banking at all. PRG ROM is 16 KB or 32 KB at $8000,
 * CHR ROM/RAM is 8 KB at $0000. May have battery-backed WRAM at $6000-$7FFF.
 */
// NROM - no banking

var utils = require("../utils");

// Nintendo's "Port test controller" (NES-INP-01): the two-plug jig that the
// factory Port Test cartridge -- and the Control Deck Test's INPUT PORT test --
// checks for. It reports its two standard controllers on bit 0 as usual and
// drives a fixed alternating signature on D3 and D4 of both ports from its own
// shift registers. D4 taps Q8 and D3 taps Q6, so D3 shows what D4 will show two
// reads later. It is a test fixture, so it is only plugged in when asked:
// nes.opts.portTestController, read in reset().
// https://www.nesdev.org/wiki/Port_test_controller
var PORT_TEST_SIGNATURE = {
  1: [1, 0, 1, 0, 1, 0, 1, 0],
  2: [0, 1, 0, 1, 0, 1, 0, 1],
};

// What the signature registers shift in once their eight bits are gone: an
// official controller (and this jig) holds Serial In low, and the console
// inverts, so joypad 1 keeps reporting 1 and joypad 2 keeps reporting 0.
var PORT_TEST_TAIL = { 1: 1, 2: 0 };

function register(Mappers) {
  Mappers[0] = function (nes) {
    this.nes = nes;
  };

  Mappers[0].prototype = {
    // ---- state ----

    reset: function () {
      this.joy1StrobeState = 0;
      this.joy2StrobeState = 0;
      this.joypadLastWrite = 0;

      // Read from the console options here, like the PPU's PAL length, so that
      // flipping nes.opts.portTestController and calling reset() plugs or
      // unplugs the jig.
      this.portTestController = this.nes.opts.portTestController === true;

      this.zapperFired = false;
      this.zapperX = null;
      this.zapperY = null;

      this.currentVromBank = 0;
      this.currentVromBank1 = 0;
    },

    // ---- CPU memory map ----

    write: function (address, value) {
      if (address < 0x2000) {
        // $0000-$1FFF: 2 KB internal CPU work RAM, mirrored every $0800.
        // This is NOT the cartridge's CHR RAM (that only exists on the PPU bus
        // and is filled through $2006/$2007). Diverting writes here to CHR RAM
        // used to throw the stack away on CHR-RAM cartridges.
        this.nes.cpu.mem[address & 0x7ff] = value;
      } else if (address > 0x4017) {
        // $4018-$FFFF: PRG ROM or cartridge WRAM (usually battery-backed at $6000-$7FFF).
        this.nes.cpu.mem[address] = value;
        if (address >= 0x6000 && address < 0x8000) {
          this.writeBatteryRam(address, value);
        }
      } else if (address > 0x2007 && address < 0x4000) {
        // $2008-$3FFF: PPU register mirrors (every 8 bytes).
        this.regWrite(0x2000 + (address & 0x7), value);
      } else {
        // $2000-$2007, $4000-$4017: PPU and APU registers.
        this.regWrite(address, value);
      }
    },

    writelow: function (address, value) {
      this.write(address, value);
    },

    load: function (address) {
      // Wrap around:
      address &= 0xffff;

      // Check address range:
      if (address > 0x4017) {
        // $4018-$FFFF: PRG ROM or cartridge WRAM.
        return this.nes.cpu.mem[address];
      } else if (address >= 0x2000) {
        // $2000-$4017: I/O Ports (PPU/APU registers).
        return this.regLoad(address);
      } else {
        // $0000-$1FFF: RAM (mirrored). This has to be the mirror of the work RAM
        // that `write` stores into, whatever the cartridge carries: the CPU side
        // of the bus never sees the PPU's CHR RAM. Reading vramMem here instead
        // left CHR-RAM games with an all-zero zero page, an unusable stack and
        // an empty nametable.
        return this.nes.cpu.mem[address & 0x7ff];
      }
    },

    regLoad: function (address) {
      switch (address >> 12) {
        case 0:
        case 1:
          break;

        case 2:
        // Fall through to case 3
        case 3:
          // PPU Registers
          switch (address & 0x7) {
            case 0x0:
              // 0x2000: PPU Control Register 1. The value is stored both in main
              // memory and in the PPU as flags. (not in the real NES)
              return this.nes.cpu.mem[0x2000];

            case 0x1:
              // 0x2001: PPU Control Register 2.
              return this.nes.cpu.mem[0x2001];

            case 0x2:
              // 0x2002: PPU Status Register.
              return this.nes.ppu.readStatusRegister();

            case 0x3:
              return 0;

            case 0x4:
              // 0x2004: Sprite Memory read.
              return this.nes.ppu.sramLoad();
            case 0x5:
              return 0;

            case 0x6:
              return 0;

            case 0x7:
              // 0x2007: VRAM read:
              return this.nes.ppu.vramLoad();
          }
          break;

        case 4:
          // Sound+Joypad registers
          switch (address - 0x4015) {
            case 0:
              // 0x4015: Sound channel enable, DMC Status
              return this.nes.papu.readReg(address);

            case 1:
              // 0x4016: Joystick 1 + Strobe
              return this.joy1Read();

            case 2:
              // 0x4017: Joystick 2 + Strobe
              // https://wiki.nesdev.com/w/index.php/Zapper
              //
              // D3 and D4 of this register belong to whatever is plugged into
              // port 2 (the Zapper's light sensor and trigger, the Port test
              // controller's signature). With no such device the lines are
              // undriven and keep the $40 of the port address's high byte, i.e.
              // both read 0 -- they must not be driven just because a Zapper
              // *could* be there.
              var w = 0;
              var zapperConnected =
                this.zapperX !== null ||
                this.zapperY !== null ||
                this.zapperFired;

              if (zapperConnected) {
                if (
                  this.zapperX !== null &&
                  this.zapperY !== null &&
                  this.nes.ppu.isPixelWhite(this.zapperX, this.zapperY)
                ) {
                  w = 0;
                } else {
                  w = 0x1 << 3;
                }

                if (this.zapperFired) {
                  w |= 0x1 << 4;
                }
              }
              return (this.joy2Read() | w) & 0xffff;
          }
          break;
      }
      return 0;
    },

    regWrite: function (address, value) {
      switch (address) {
        case 0x2000:
          // PPU Control register 1
          this.nes.cpu.mem[address] = value;
          this.nes.ppu.updateControlReg1(value);
          break;

        case 0x2001:
          // PPU Control register 2
          this.nes.cpu.mem[address] = value;
          this.nes.ppu.updateControlReg2(value);
          break;

        case 0x2003:
          // Set Sprite RAM address:
          this.nes.ppu.writeSRAMAddress(value);
          break;

        case 0x2004:
          // Write to Sprite RAM:
          this.nes.ppu.sramWrite(value);
          break;

        case 0x2005:
          // Screen Scroll offsets:
          this.nes.ppu.scrollWrite(value);
          break;

        case 0x2006:
          // Set VRAM address:
          this.nes.ppu.writeVRAMAddress(value);
          break;

        case 0x2007:
          // Write to VRAM:
          this.nes.ppu.vramWrite(value);
          break;

        case 0x4014:
          // Sprite Memory DMA Access
          this.nes.ppu.sramDMA(value);
          break;

        case 0x4015:
          // Sound Channel Switch, DMC Status
          this.nes.papu.writeReg(address, value);
          break;

        case 0x4016:
          // Joystick 1 + Strobe
          if ((value & 1) === 0 && (this.joypadLastWrite & 1) === 1) {
            this.joy1StrobeState = 0;
            this.joy2StrobeState = 0;
          }
          this.joypadLastWrite = value;
          break;

        case 0x4017:
          // Sound channel frame sequencer:
          this.nes.papu.writeReg(address, value);
          break;

        default:
          // Sound registers
          if (address >= 0x4000 && address <= 0x4017) {
            this.nes.papu.writeReg(address, value);
          }
      }
    },

    setMirroring: function (mirroring) {
      this.nes.ppu.setMirroring(mirroring);
    },

    // ---- controllers ----

    // $4016/$4017 shift out one button bit per read. Hardware model (NESdev,
    // "Standard controller"):
    //
    // - while the strobe -- bit 0 of the last $4016 write -- is high, the 4021
    //   is reloaded continuously, so every read reports button A again and no
    //   bit position is consumed;
    // - with the strobe low, reads 0-7 are A, B, Select, Start, Up, Down, Left,
    //   Right;
    // - from the ninth read on, an official controller reports 1 forever: its
    //   Serial In is tied to ground and the console inverts the data line. The
    //   top bits of the register are undriven, so they keep the $40 of the port
    //   address's high byte that was left on the bus: an idle pad reads $40 for
    //   its eight bits, then $41. Paperboy is documented to need exactly those
    //   values.
    //
    // Reads 9-19 used to answer 0, read 20 used to answer 1, and the bit counter
    // wrapped at 24 reads, which handed a game that never re-strobes fresh
    // button data instead of the trailing 1s hardware gives it.

    // D3/D4 of a port as driven by the Port test controller, or 0 (undriven)
    // when no such device is plugged in.
    portTestBits: function (port, bit) {
      if (!this.portTestController) {
        return 0;
      }

      var signature = PORT_TEST_SIGNATURE[port];
      var bit3 =
        bit + 2 < signature.length ? signature[bit + 2] : PORT_TEST_TAIL[port];
      var bit4 = bit < signature.length ? signature[bit] : PORT_TEST_TAIL[port];

      return (bit4 << 4) | (bit3 << 3);
    },

    joy1Read: function () {
      if (this.joypadLastWrite & 1) {
        // Strobe high: reloaded continuously, so this is always button A.
        return this.nes.controllers[1].state[0] | this.portTestBits(1, 0);
      }

      if (this.joy1StrobeState < 8) {
        var bit = this.joy1StrobeState;
        this.joy1StrobeState++;
        return this.nes.controllers[1].state[bit] | this.portTestBits(1, bit);
      }

      return 0x41 | this.portTestBits(1, this.joy1StrobeState);
    },

    joy2Read: function () {
      if (this.joypadLastWrite & 1) {
        // Strobe high: reloaded continuously, so this is always button A.
        return this.nes.controllers[2].state[0] | this.portTestBits(2, 0);
      }

      if (this.joy2StrobeState < 8) {
        var bit = this.joy2StrobeState;
        this.joy2StrobeState++;
        return this.nes.controllers[2].state[bit] | this.portTestBits(2, bit);
      }

      return 0x41 | this.portTestBits(2, this.joy2StrobeState);
    },

    // ---- power on ----

    loadROM: function () {
      if (!this.nes.rom.valid || this.nes.rom.romCount < 1) {
        throw new Error("NROM: Invalid ROM! Unable to load.");
      }

      this.reset();

      // Load ROM into memory:
      this.loadPRGROM();

      // Load CHR-ROM:
      this.loadCHRROM();

      // Load Battery RAM (if present):
      this.loadBatteryRam();

      // Load the trainer, which lives at $7000:
      this.loadTrainer();

      // Reset IRQ:
      this.resetIrqHandler();
    },

    // Load PRG ROM into the CPU address space. NROM has either 1 or 2 16 KB banks.
    // If 2 banks: first at $8000, second at $C000.
    // If 1 bank: mirrored at both $8000 and $C000 (the reset vector is at $FFFC).
    loadPRGROM: function () {
      if (this.nes.rom.romCount > 1) {
        // Load the two first banks into memory.
        this.loadRomBank(0, 0x8000);
        this.loadRomBank(1, 0xc000);
      } else {
        // Load the one bank into both memory locations:
        this.loadRomBank(0, 0x8000);
        this.loadRomBank(0, 0xc000);
      }
    },

    // Load CHR ROM into the PPU pattern table memory ($0000-$1FFF).
    // If 1 bank (8 KB): mirror it into both pattern table halves ($0000 and $1000).
    // If >1 bank: load bank 0 at $0000, bank 1 at $1000.
    loadCHRROM: function () {
      if (this.nes.rom.vromCount > 0) {
        if (this.nes.rom.vromCount === 1) {
          this.loadVromBank(0, 0x0000);
          this.loadVromBank(0, 0x1000);
        } else {
          this.loadVromBank(0, 0x0000);
          this.loadVromBank(1, 0x1000);
        }
      }
      // With no CHR-ROM the PPU keeps its own zeroed 8 KB of pattern RAM.
    },

    loadBatteryRam: function () {
      var ram = this.nes.rom.batteryRamData;
      if (ram !== null && ram !== undefined && ram.length > 0) {
        utils.copyArrayElements(ram, 0, this.nes.cpu.mem, 0x6000, 0x2000);
      }
    },

    loadTrainer: function () {
      var trainer = this.nes.rom.trainerData;
      if (trainer !== null && trainer !== undefined && trainer.length === 512) {
        utils.copyArrayElements(trainer, 0, this.nes.cpu.mem, 0x7000, 512);
      }
    },

    writeBatteryRam: function (address, value) {
      var ram = this.nes.rom.batteryRamData;
      if (ram !== null && ram !== undefined) {
        ram[address - 0x6000] = value;
      }
      if (typeof this.nes.opts.onBatteryRamWrite === "function") {
        this.nes.opts.onBatteryRamWrite(address, value);
      }
    },

    resetIrqHandler: function () {
      this.nes.cpu.requestIrq(this.nes.cpu.IRQ_RESET);
    },

    // ---- banking helpers ----
    //
    // All banking helpers copy data from ROM arrays into the CPU/PPU memory maps.
    // They use utils.copyArrayElements which is a fast typed-array-aware memcpy.
    //
    // BANK SIZE CONVENTIONS (used by all mappers):
    // - 16 KB PRG bank: loadRomBank(bank16k, address) where address is $8000/$A000/$C000/$E000
    // - 8 KB PRG bank:  load8kRomBank(bank8k, address) where address is $8000/$A000/$C000/$E000
    // - 32 KB PRG bank: load32kRomBank(bank32k, address) fills $8000-$FFFF (two 16 KB halves)
    // - 4 KB CHR bank:  loadVromBank(bank4k, address) where address is $0000/$0400/$0800/$0C00/$1000/$1400/$1800/$1C00
    // - 8 KB CHR bank:  load8kVromBank(bank8k, address) fills $0000 or $1000 (two 4 KB halves)
    // - 1 KB CHR bank:  load1kVromBank(bank1k, address) fills one 1 KB window
    //
    // BANK NUMBERING: The "bank" parameter always refers to the logical bank number
    // in the ROM file (0 = first 16 KB PRG bank, 0 = first 4 KB CHR bank, etc.).
    // The helpers handle modulo wrapping automatically.

    // Load a 16 KB PRG ROM bank into CPU memory at the given address ($8000, $A000, $C000, $E000).
    loadRomBank: function (bank, address) {
      var rom = this.nes.rom;
      if (!rom || !rom.rom || rom.rom.length === 0) {
        return;
      }
      // Loads a ROM bank into the specified address.
      bank %= rom.romCount;
      var data = rom.rom[bank];
      utils.copyArrayElements(data, 0, this.nes.cpu.mem, address, data.length);
    },

    // Load a 4 KB CHR ROM bank into PPU pattern table memory at the given address.
    // Also refreshes the decoded tile cache (ptTile) used by the renderer.
    loadVromBank: function (bank, address) {
      var rom = this.nes.rom;
      if (rom.vromCount === 0) {
        return;
      }
      this.nes.ppu.triggerRendering();

      var index = bank % rom.vromCount;
      utils.copyArrayElements(
        rom.vrom[index],
        0,
        this.nes.ppu.vramMem,
        address,
        4096,
      );

      var vromTile = rom.vromTile[index];
      utils.copyArrayElements(
        vromTile,
        0,
        this.nes.ppu.ptTile,
        address >> 4, // each tile is 16 bytes, 256 tiles per 4 KB bank
        256,
      );
    },

    // Load a 32 KB PRG ROM bank (two consecutive 16 KB banks) into $8000-$FFFF.
    // Used by AxROM (mapper 7), BNROM (34), MROM (8), etc.
    load32kRomBank: function (bank, address) {
      this.loadRomBank((bank * 2) % this.nes.rom.romCount, address);
      this.loadRomBank((bank * 2 + 1) % this.nes.rom.romCount, address + 16384);
    },

    // Load an 8 KB PRG ROM bank (half of a 16 KB bank) into CPU memory.
    // bank8k = 0,1,2,3... corresponds to first half of bank 0, second half of bank 0,
    // first half of bank 1, etc. This is the granularity MMC3 uses for PRG banking.
    load8kRomBank: function (bank8k, address) {
      var rom = this.nes.rom;
      if (rom.romCount === 0) {
        return;
      }
      var bank16k = Math.floor(bank8k / 2) % rom.romCount;
      var offset = (bank8k % 2) * 8192;

      utils.copyArrayElements(
        rom.rom[bank16k],
        offset,
        this.nes.cpu.mem,
        address,
        8192,
      );
    },

    // Load an 8 KB CHR ROM bank (two consecutive 4 KB banks) into PPU memory.
    // Used by CNROM (mapper 3), MMC1 8 KB mode (mapper 1), etc.
    load8kVromBank: function (bank4kStart, address) {
      if (this.nes.rom.vromCount === 0) {
        return;
      }
      this.loadVromBank(bank4kStart % this.nes.rom.vromCount, address);
      this.loadVromBank(
        (bank4kStart + 1) % this.nes.rom.vromCount,
        address + 4096,
      );
    },

    // Load a single 4 KB CHR ROM bank into PPU memory.
    // Used by MMC1 4 KB CHR mode, MMC3 CHR banking, etc.
    load4kVromBank: function (bank4k, address) {
      if (this.nes.rom.vromCount === 0) {
        return;
      }
      this.loadVromBank(bank4k % this.nes.rom.vromCount, address);
    },

    // Load a 2 KB CHR ROM bank (half of a 4 KB bank) into PPU memory.
    // Used by MMC3 CHR banking (registers 0-5 are 1 KB but loaded in pairs via load2kVromBank internally).
    load2kVromBank: function (bank2k, address) {
      if (this.nes.rom.vromCount === 0) {
        return;
      }

      var rom = this.nes.rom;
      var bank4k = Math.floor(bank2k / 2) % rom.vromCount;
      var bankoffset = (bank2k % 2) * 2048;
      this.nes.ppu.triggerRendering();

      utils.copyArrayElements(
        rom.vrom[bank4k],
        bankoffset,
        this.nes.ppu.vramMem,
        address,
        2048,
      );

      var vromTile = rom.vromTile[bank4k];
      var baseIndex = address >> 4;
      for (var i = 0; i < 128; i++) {
        // 128 tiles per 2 KB bank (each tile = 16 bytes, 2048/16 = 128)
        this.nes.ppu.ptTile[baseIndex + i] = vromTile[(bank2k % 2 << 7) + i];
      }
    },

    // Load a 1 KB CHR ROM bank (quarter of a 4 KB bank) into PPU memory.
    // Used by MMC3 (registers 0-5 are 1 KB banks) and MMC4/MMC2.
    load1kVromBank: function (bank1k, address) {
      if (this.nes.rom.vromCount === 0) {
        return;
      }

      var rom = this.nes.rom;
      var bank4k = Math.floor(bank1k / 4) % rom.vromCount;
      var bankoffset = (bank1k % 4) * 1024;
      this.nes.ppu.triggerRendering();

      utils.copyArrayElements(
        rom.vrom[bank4k],
        bankoffset,
        this.nes.ppu.vramMem,
        address,
        1024,
      );

      // Update tiles:
      var vromTile = rom.vromTile[bank4k];
      var baseIndex = address >> 4;
      for (var i = 0; i < 64; i++) {
        // 64 tiles per 1 KB bank (each tile = 16 bytes, 1024/16 = 64)
        this.nes.ppu.ptTile[baseIndex + i] = vromTile[(bank1k % 4 << 6) + i];
      }
    },

    // ---- hooks for boards with a scanline counter ----

    // How often NES.frame() clocks `clockIrqCounter`:
    //   "a12"      from ppu.serviceA12(), once per PPU dot, on the rising edges
    //              of the PPU address bit A12. A game loads its IRQ latch in
    //              those clocks, so once per scanline would make every IRQ
    //              arrive tens of scanlines too late.
    //   "scanline" once per finished scanline, which is what the MMC5 compares
    //              its target scanline against.
    irqClockRate: "scanline",

    // True for the chips that count PPU address bit A12 (MMC3, MMC4, MMC2).
    // ppu.serviceA12() teste ce drapeau avant chaque dot : les autres puces ne
    // payent rien.
    watchesA12: false,

    // True for the chips whose IRQ counter runs on CPU cycles (FME-7).
    // NES.frame() teste ce drapeau apres chaque salve du CPU.
    watchesCpuCycles: false,

    clockCpuCycles: function (cycles) {
      // Does nothing. This is used by the FME-7.
    },

    // The PPU reports an A12 transition, with the number of dots just spent low.
    // The MMC3's filter only lets an edge through after a long enough low
    // period, so that count is what it needs to decide.
    notifyA12: function (level, lowDots) {
      // Does nothing. This is used by the MMC3, MMC4 and MMC2.
    },

    clockIrqCounter: function () {
      // Does nothing. This is used by the MMC3 mapper.
    },

    latchAccess: function (address) {
      // Does nothing. This is used by MMC2.
    },

    toJSON: function () {
      return {
        joy1StrobeState: this.joy1StrobeState,
        joy2StrobeState: this.joy2StrobeState,
        joypadLastWrite: this.joypadLastWrite,
      };
    },

    fromJSON: function (s) {
      this.joy1StrobeState = s.joy1StrobeState;
      this.joy2StrobeState = s.joy2StrobeState;
      this.joypadLastWrite = s.joypadLastWrite;
    },
  };
}

module.exports = { register: register };
